import React, { useState } from 'react'
import * as yup from 'yup'
import { TextField, FilledTextFieldProps, IconButton, InputAdornment } from '@material-ui/core'
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@material-ui/icons'
import Requirement from './components/Requirement'

export interface Props extends FilledTextFieldProps {
  requirementsVisible?: boolean
  touched?: boolean
}

export const passwordValidationSchema = yup
  .string()
  .min(8, 'At least 8 characters')
  .matches(/((?=.*[A-Z])(?=.*[a-z]))/, 'Upper and lower case letters')
  .matches(/((?=.*[0-9])(?=.*[!@#$%^&*]))/, 'Contains a number and a symbol (!@#$%^&*)')
  .required('Your password is required')

const PasswordInput = ({
  id,
  label,
  error,
  disabled,
  requirementsVisible = false,
  touched = false,
  helperText,
  value,
  ...rest
}: Props): JSX.Element => {
  const [passwordVisible, setPasswordVisible] = useState(false)

  const handleVisibilityClick = (): void => {
    setPasswordVisible(isVisible => !isVisible)
  }

  const renderHelperText = (): JSX.Element | React.ReactNode => {
    if (requirementsVisible) {
      const atLeast8Characters = (value as string).length >= 8
      const upperAndLowerCaseLetters = /((?=.*[A-Z])(?=.*[a-z]))/.test(value as string)
      const containsNumberAndSymbol = /((?=.*[0-9])(?=.*[!@#$%^&*]))/.test(value as string)

      return (
        <>
          <Requirement
            touched={touched || Boolean((value as string).length)}
            valid={atLeast8Characters}
          >
            At least 8 characters
          </Requirement>

          <Requirement
            touched={touched || Boolean((value as string).length)}
            valid={upperAndLowerCaseLetters}
          >
            Upper and lower case letters
          </Requirement>
          <Requirement
            touched={touched || Boolean((value as string).length)}
            valid={containsNumberAndSymbol}
          >
            Contains a number and a symbol. Example: !@#$%^&*
          </Requirement>
        </>
      )
    }

    return helperText
  }

  return (
    <TextField
      {...rest}
      id={id}
      label={label}
      error={error}
      disabled={disabled}
      margin="normal"
      variant="filled"
      type={passwordVisible ? 'text' : 'password'}
      helperText={renderHelperText()}
      fullWidth
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton aria-label="toggle password visibility" onClick={handleVisibilityClick}>
              {passwordVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  )
}

export default PasswordInput
